/**
 * Task schemas. A Task is a unit of crew work — Pickup, Install, Punch,
 * Walkthrough, or Labor — scheduled within a Project.
 *
 * Each task type carries its own type-specific fields (RMA reference for an
 * RMA pickup, etc.). The wire shape uses a discriminated union on `type`.
 */
import { z } from 'zod';
import { FloorplanPinSchema, IsoDateTimeSchema, PageEnvelope, PageQuerySchema, UuidSchema, } from './common.js';
import { InstallSubtypeSchema, PickupSubtypeSchema, PunchItemStatusSchema, TaskStatusSchema, TaskTypeSchema, } from './enums.js';
// ----------------------------------------------------------------------------
// Base task shape (fields common to every type)
// ----------------------------------------------------------------------------
/** Lean assigned-crew shape for at-a-glance avatars on a task (demo #5). */
export const TaskCrewSchema = z.object({
    display_name: z.string(),
    avatar_url: z.string().url().nullable(),
});
/** Planned crew role demand for a task — head count per role. Distinct from the
 *  bid's CrewRoleLines (which also carry hours); this is "who this task needs",
 *  set by the supervisor "Plan the work" studio and read by dispatch. */
export const CrewRoleCountsSchema = z.object({
    lead: z.number().int().min(0).max(99),
    installer: z.number().int().min(0).max(99),
    driver: z.number().int().min(0).max(99),
    trainee: z.number().int().min(0).max(99),
});
const TaskBaseSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    title: z.string().min(1).max(200),
    description: z.string().nullable(),
    status: TaskStatusSchema.default('scheduled'),
    // Schedule window
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    estimated_duration_minutes: z.number().int().min(0).nullable(),
    // Execution timestamps
    started_at: IsoDateTimeSchema.nullable(),
    paused_at: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    // Location override (defaults to project's site)
    location_id: UuidSchema.nullable(),
    // Capacity demand (crew_size_needed)
    crew_size_needed: z.number().int().min(0).max(20).default(1),
    /** Planned per-role crew demand (post-sixth-demo studio); null for legacy or
     *  default-seeded tasks. The scalar above stays the total head count. */
    crew_roles_needed: CrewRoleCountsSchema.nullable().default(null),
    /** A PM-requested task awaiting supervisor confirmation (Calendar Phase 4b).
     *  Excluded from dispatch / project-status rollup until confirmed. */
    pending_confirmation: z.boolean().default(false),
    /** Optional note the requester attached, shown to the supervisor at confirm. */
    request_note: z.string().nullable().default(null),
    /** Assigned crew (deduped) — populated on the project task list for avatars
     *  (demo #5); other task endpoints may omit it → defaults to empty. */
    crew: z.array(TaskCrewSchema).optional().default([]),
    // Audit
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
// ----------------------------------------------------------------------------
// Type-specific extensions
// ----------------------------------------------------------------------------
/**
 * A single pickup stop on a pickup task. The array's order is the pickup
 * sequence (reorderable). Each stop is either a Vari warehouse (warehouse_id
 * set, with a snapshot of its address) or a custom address geocoded via Azure
 * Maps. Address fields are lenient (nullable) so a warehouse without a full
 * structured address or a sparse custom hit still stores — `name` is the
 * display label and lat/lng drive the crew's map link.
 */
export const PickupLocationSchema = z.object({
    warehouse_id: UuidSchema.nullable().default(null),
    name: z.string().min(1).max(200),
    line1: z.string().max(200).nullable().default(null),
    line2: z.string().max(200).nullable().default(null),
    city: z.string().max(100).nullable().default(null),
    state: z.string().max(20).nullable().default(null),
    postal_code: z.string().max(20).nullable().default(null),
    latitude: z.number().min(-90).max(90).nullable().default(null),
    longitude: z.number().min(-180).max(180).nullable().default(null),
});
export const PickupTaskSchema = TaskBaseSchema.extend({
    type: z.literal('pickup'),
    subtype: PickupSubtypeSchema,
    sap_rma_reference: z
        .string()
        .nullable()
        .describe('Required when subtype=rma; null otherwise'),
    truck_label: z.string().nullable(),
    /** Ordered pickup stops (warehouse or custom address). */
    pickup_locations: z.array(PickupLocationSchema).default([]),
});
export const InstallTaskSchema = TaskBaseSchema.extend({
    type: z.literal('install'),
    subtype: InstallSubtypeSchema,
});
export const PunchTaskSchema = TaskBaseSchema.extend({
    type: z.literal('punch'),
    punch_items_total: z.number().int().min(0).default(0),
    punch_items_open: z.number().int().min(0).default(0),
});
export const LaborTaskSchema = TaskBaseSchema.extend({
    type: z.literal('labor'),
});
// ----------------------------------------------------------------------------
// Discriminated union — the wire shape for any task
// ----------------------------------------------------------------------------
export const TaskSchema = z.discriminatedUnion('type', [
    PickupTaskSchema,
    InstallTaskSchema,
    PunchTaskSchema,
    LaborTaskSchema,
]);
// ----------------------------------------------------------------------------
// Create payloads (one per type to keep type-specific required fields clean)
// ----------------------------------------------------------------------------
const TaskCreateBase = z.object({
    project_id: UuidSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(10_000).optional().nullable(),
    scheduled_start: IsoDateTimeSchema.optional().nullable(),
    scheduled_end: IsoDateTimeSchema.optional().nullable(),
    estimated_duration_minutes: z.number().int().min(0).optional().nullable(),
    location_id: UuidSchema.optional().nullable(),
    crew_size_needed: z.number().int().min(0).max(20).default(1),
    /** Requester's note for the supervisor (PM calendar request). */
    request_note: z.string().max(2000).optional().nullable(),
});
export const TaskCreateSchema = z.discriminatedUnion('type', [
    TaskCreateBase.extend({
        type: z.literal('pickup'),
        subtype: PickupSubtypeSchema,
        sap_rma_reference: z.string().optional().nullable(),
        truck_label: z.string().optional().nullable(),
        pickup_locations: z.array(PickupLocationSchema).optional().default([]),
    }),
    TaskCreateBase.extend({
        type: z.literal('install'),
        subtype: InstallSubtypeSchema,
    }),
    TaskCreateBase.extend({
        type: z.literal('punch'),
    }),
    TaskCreateBase.extend({
        type: z.literal('labor'),
    }),
]);
// ----------------------------------------------------------------------------
// Intake task suggestion — a project_id-less task the AI intake proposes from
// the fused PDFs (D.1b). The PM confirms/edits these in the review screen;
// each kept row becomes a TaskCreateInput once the project exists.
// ----------------------------------------------------------------------------
export const IntakeProposedTaskSchema = z.object({
    type: z.enum(['pickup', 'install', 'punch', 'labor']),
    /** 'warehouse'|'supplier'|'rma' for pickup; 'new'|'reconfigure' for install. */
    subtype: z.string().nullable(),
    title: z.string().min(1).max(200),
    crew_size_needed: z.number().int().min(0).max(20),
    estimated_duration_minutes: z.number().int().min(0).nullable(),
    /** Crew-facing task description (→ tasks.description). Optional: the AI/default
     *  proposers omit it; the supervisor "Plan the work" studio sets it. */
    description: z.string().max(10_000).nullable().optional(),
    /** Planned per-role crew demand from the studio (→ tasks.crew_roles_needed). */
    crew_roles_needed: CrewRoleCountsSchema.nullable().optional(),
    /** The day + working window the studio placed this task on; the create route
     *  builds the task's scheduled_start/end from it (business tz). Optional —
     *  unplaced tasks fall back to the auto-seed. */
    placement: z
        .object({ date: z.string(), start_time: z.string(), end_time: z.string() })
        .nullable()
        .optional(),
});
// ----------------------------------------------------------------------------
// Labor estimate (D.2) — rough crew-hours from task type + scope, compared to
// the delivery/install budget so an over-budget job gets flagged before Leo
// commits. A defensible heuristic (explainable in the field), not an LLM guess.
// ----------------------------------------------------------------------------
/**
 * Fallback crew cost per person-hour, in cents. The real rates are
 * per-role and admin-configurable (see ./labor-rates and the labor_rates
 * table); this constant is only the default when a caller hasn't loaded them
 * yet (e.g. the estimate before the rates request resolves).
 */
export const LABOR_RATE_CENTS_PER_HOUR = 7500;
/** Rough wall-clock minutes for a task of the given type. */
export function estimateTaskMinutes(type, opts) {
    const items = Math.max(0, opts?.lineItemCount ?? 0);
    switch (type) {
        case 'install':
            // Base setup + ~30 min per line item (panels / desks / …), capped.
            return Math.min(150 + 30 * items, 600);
        case 'pickup':
            return 60;
        case 'punch':
            return 90;
        case 'labor':
            return 120;
    }
}
/**
 * Crew-hours + labor cost for a set of proposed tasks, vs the budget.
 *
 * The estimate runs before crew is assigned, so it can't know the role mix —
 * `rateCentsPerHour` is the blended/default rate (the admin-set
 * `estimate_default`), falling back to LABOR_RATE_CENTS_PER_HOUR when the
 * caller hasn't loaded the configured rates yet.
 */
export function summarizeLabor(items, budgetCents, rateCentsPerHour = LABOR_RATE_CENTS_PER_HOUR) {
    let taskMinutes = 0;
    let personMinutes = 0;
    for (const it of items) {
        // Skeleton tasks (no duration estimate + no crew) are intentionally unplanned
        // — they contribute 0 man-hours until the supervisor plans them (demo #7).
        if (it.estimated_duration_minutes == null && it.crew_size_needed === 0)
            continue;
        const minutes = it.estimated_duration_minutes ?? estimateTaskMinutes(it.type);
        taskMinutes += minutes;
        personMinutes += minutes * Math.max(1, it.crew_size_needed);
    }
    const personHours = personMinutes / 60;
    const laborCents = Math.round(personHours * rateCentsPerHour);
    const budget = budgetCents ?? null;
    return {
        total_task_minutes: taskMinutes,
        total_person_hours: Math.round(personHours * 10) / 10,
        estimated_labor_cents: laborCents,
        budget_cents: budget,
        budget_ratio: budget && budget > 0 ? laborCents / budget : null,
        over_budget: budget != null && laborCents > budget,
        headroom_cents: budget != null ? budget - laborCents : null,
    };
}
/**
 * The default task spine to propose for a project of a given kind (D.1c),
 * with per-task labor estimates (D.2). Pure + shared by the API intake (initial
 * proposal, with the sales-order line-item count) and the web review
 * (re-proposal on kind change), so both stay in lock-step.
 */
export function defaultTasksForKind(kind, clientLabel, opts) {
    const label = clientLabel.trim() || 'job';
    // O-7 (FMS feedback 2026-08-11): pickups are now their OWN schedulable object
    // (workstream C), not a seeded task — so intake no longer proposes a pickup
    // task. Install-kind seeds just the install; a pickup-kind project seeds no
    // tasks (the crew work a pickup object, or a project block, directly). The
    // `type:'pickup'` task still exists (existing rows + manual add), so the
    // status rollup keeps its pickup carve-out. `opts.originWarehouses` is now
    // unused here — the pickup object's stops reference warehouses instead.
    const install = (subtype) => ({
        type: 'install',
        subtype,
        title: `${subtype === 'reconfigure' ? 'Reconfigure' : 'Install'} — ${label}`,
        crew_size_needed: 2,
        estimated_duration_minutes: estimateTaskMinutes('install', {
            lineItemCount: opts?.lineItemCount,
        }),
    });
    switch (kind) {
        case 'install':
            return [install('new')];
        case 'reconfigure':
            return [install('reconfigure')];
        case 'punch':
            return [
                {
                    type: 'punch',
                    subtype: null,
                    title: `Punch list — ${label}`,
                    crew_size_needed: 1,
                    estimated_duration_minutes: estimateTaskMinutes('punch'),
                },
            ];
        case 'service':
            return [
                {
                    type: 'labor',
                    subtype: null,
                    title: `Service — ${label}`,
                    crew_size_needed: 1,
                    estimated_duration_minutes: estimateTaskMinutes('labor'),
                },
            ];
        case 'pickup':
            // A pickup-kind project seeds no tasks (O-7): the run is a pickup object.
            return [];
        case 'other':
            return [];
    }
}
// ----------------------------------------------------------------------------
// Update payloads
// ----------------------------------------------------------------------------
export const TaskUpdateSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(10_000).optional().nullable(),
    // NOTE: task status is intentionally NOT editable here. Status changes must go
    // through POST /tasks/:id/transition, which enforces the allowed-transition
    // machine, writes the task_status_event (labor actuals + recap timeline),
    // stamps started_at/completed_at, rolls the change up to the project, and
    // fires notifications. A raw status set here would bypass all of that (review #9).
    scheduled_start: IsoDateTimeSchema.optional().nullable(),
    scheduled_end: IsoDateTimeSchema.optional().nullable(),
    estimated_duration_minutes: z.number().int().min(0).optional().nullable(),
    location_id: UuidSchema.optional().nullable(),
    crew_size_needed: z.number().int().min(0).max(20).optional(),
    /** Booked vehicle from the fleet (ADR-0019); null clears the booking. */
    vehicle_id: UuidSchema.optional().nullable(),
    /** Ordered pickup stops (pickup tasks) — add/remove/reorder. */
    pickup_locations: z.array(PickupLocationSchema).optional(),
    /**
     * Supervisor/admin override of the recorded execution times — a post-hoc fix
     * for a forgotten Start/Finish that inflated (or mis-stated) the elapsed time.
     * The route validates completed_at ≥ started_at against the resulting pair.
     */
    started_at: IsoDateTimeSchema.optional().nullable(),
    completed_at: IsoDateTimeSchema.optional().nullable(),
});
// ----------------------------------------------------------------------------
// Status transitions (used by mobile crew's En route / On site / Complete taps)
// ----------------------------------------------------------------------------
export const TaskStatusTransitionSchema = z.object({
    to: TaskStatusSchema,
    occurred_at: IsoDateTimeSchema.optional().describe('defaults to server now'),
    note: z.string().max(2_000).optional(),
    // Captured for the "En route" → "On site" transitions (one-time GPS — see ADR-0010)
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
});
// ----------------------------------------------------------------------------
// Task assignments
// ----------------------------------------------------------------------------
export const TaskAssignmentSchema = z.object({
    id: UuidSchema,
    task_id: UuidSchema,
    crew_member_id: UuidSchema,
    is_lead: z.boolean().default(false),
    // Assignment block (ADR-0013): the scheduled truth — start/end/truck — lives
    // on the assignment, not the task. Nullable until the block is scheduled.
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    truck_label: z.string().nullable(),
    assigned_at: IsoDateTimeSchema,
});
export const TaskAssignmentCreateSchema = z.object({
    task_id: UuidSchema,
    crew_member_id: UuidSchema,
    is_lead: z.boolean().default(false),
});
/**
 * Drag-drop reassignment payload. The supervisor drags a task bar from
 * one crew row to another (or to/from the Unassigned lane).
 *
 *   - from + to    → swap the assignment from one crew member to another
 *   - from + null  → remove the assignment (task drifts to Unassigned)
 *   - null + to    → add an assignment (task picks up a new crew member)
 *
 * At least one of from / to is required.
 */
export const TaskReassignSchema = z
    .object({
    from_crew_member_id: UuidSchema.nullable().optional(),
    to_crew_member_id: UuidSchema.nullable().optional(),
})
    .refine((v) => v.from_crew_member_id || v.to_crew_member_id, {
    message: 'At least one of from_crew_member_id or to_crew_member_id is required',
});
/**
 * Set the full crew set on a task at once (SCH.6 assign-crew picker) — the API
 * reconciles inserts/removals + the lead against what's there. Empty array
 * unassigns everyone.
 */
export const TaskAssignmentInputSchema = z.object({
    crew_member_id: UuidSchema,
    is_lead: z.boolean().default(false),
});
export const TaskAssignmentsSetSchema = z.object({
    assignments: z.array(TaskAssignmentInputSchema).max(20),
    /** Supervisor-confirmed reassign: resolve a time-slot conflict by MOVING the
     *  crew member off the overlapping task(s) onto this one, instead of rejecting
     *  the double-booking. */
    reassign_conflicts: z.boolean().optional(),
});
// ----------------------------------------------------------------------------
// Punch items (sub-records under a punch task)
// ----------------------------------------------------------------------------
export const PunchItemSchema = z.object({
    id: UuidSchema,
    task_id: UuidSchema.describe('the parent punch task'),
    title: z.string().min(1).max(200),
    description: z.string().nullable(),
    status: PunchItemStatusSchema.default('open'),
    blocks_recap: z
        .boolean()
        .default(false)
        .describe('surfaced as a closeout warning; closing with open punch stays allowed (VCS-501) — the punch task lives on as a schedulable follow-up'),
    // Optional floorplan pin (when a design package PDF exists)
    floorplan_pin: FloorplanPinSchema.nullable(),
    // Fallback zone tag when no floorplan
    zone_tag: z.string().nullable(),
    assigned_to_user_id: UuidSchema.nullable(),
    closed_at: IsoDateTimeSchema.nullable(),
    closed_by_user_id: UuidSchema.nullable(),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const PunchItemCreateSchema = z.object({
    task_id: UuidSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(10_000).optional().nullable(),
    blocks_recap: z.boolean().default(false),
    floorplan_pin: FloorplanPinSchema.optional().nullable(),
    zone_tag: z.string().max(50).optional().nullable(),
    assigned_to_user_id: UuidSchema.optional().nullable(),
});
// ----------------------------------------------------------------------------
// List queries / responses
// ----------------------------------------------------------------------------
export const TaskListQuerySchema = PageQuerySchema.extend({
    project_id: UuidSchema.optional(),
    status: TaskStatusSchema.optional(),
    type: TaskTypeSchema.optional(),
    crew_member_id: UuidSchema.optional(),
    from_date: IsoDateTimeSchema.optional(),
    to_date: IsoDateTimeSchema.optional(),
});
export const TaskListResponseSchema = PageEnvelope(TaskSchema);
// ----------------------------------------------------------------------------
// Pending-task approval queue (Calendar Phase 4b) — PM-requested tasks awaiting
// a supervisor's confirm. Enriched with project context + who requested it, for
// the supervisor/admin Home landing card.
// ----------------------------------------------------------------------------
export const PendingTaskReviewSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string().nullable(),
    title: z.string(),
    type: TaskTypeSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    crew_size_needed: z.number().int(),
    requested_by: z.string().nullable(),
    request_note: z.string().nullable(),
    created_at: IsoDateTimeSchema,
});
export const PendingTasksResponseSchema = z.object({
    data: z.array(PendingTaskReviewSchema),
});
/**
 * Confirm a pending task (supervisor/admin). Optionally override the schedule —
 * the supervisor can accept the requested time or commit a different time/day.
 * Omit both to confirm at the requested time unchanged.
 */
export const TaskConfirmSchema = z.object({
    scheduled_start: IsoDateTimeSchema.nullable().optional(),
    scheduled_end: IsoDateTimeSchema.nullable().optional(),
});
//# sourceMappingURL=tasks.js.map